/**
 * SPEC-AdminImpersonation §"Client wiring #5" — small launcher card.
 *
 * Visible only when `isAdmin = true` AND `isImpersonating = false`. Two
 * buttons: `View as Agent` and `View as Senior`. Each starts impersonation
 * with writes OFF (the SPEC's safety default — second explicit flip enables
 * writes). After a successful start, the auth query invalidates and the
 * banner appears.
 *
 * Mounted on the admin settings page. The launcher could equally live in
 * a dropdown menu in the app header, but settings page is the natural
 * home and the SPEC's text matches it.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import { useToast } from "@/hooks/use-toast";
import { startImpersonation, type ImpersonationTargetRole } from "@/lib/impersonation";
import { Eye } from "lucide-react";

export function ImpersonationLauncher() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isAdmin } = useRole();
  const { isImpersonating } = useAuth();

  const mutation = useMutation({
    mutationFn: (role: ImpersonationTargetRole) => startImpersonation(queryClient, role),
    onError: (e: any) => {
      toast({
        title: "Could not start impersonation",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Gated: admin-only AND not currently impersonating.
  if (!isAdmin || isImpersonating) return null;

  return (
    <Card data-testid="card-impersonation-launcher">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">View as another role</CardTitle>
        </div>
        <CardDescription>
          Walk the agent or senior surfaces as that role sees them. Writes default
          to OFF — flip the banner&apos;s &quot;Toggle writes&quot; to test real mutations.
          Every write still attributes to you in the audit log.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate("agent")}
            data-testid="impersonation-launcher-agent"
          >
            View as Agent
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate("senior")}
            data-testid="impersonation-launcher-senior"
          >
            View as Senior
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ImpersonationLauncher;
